/*
 * Created Date: 20-01-2024, 11:00:52 am
 * Author: Victor
 * Email: victor@pt-corp.vn
 * -----
 * Last Modified: Thu Mar 21 2024
 * Modified By: Victor
 * -----
 * Copyright (c) 2024 PT CORP, Inc
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	----------------------------------------------------------
 */

import { useBreakpoints } from '@Core/hooks'
import { Box } from '@mui/material'
import { MathJaxContext } from 'better-react-mathjax'
import clsx from 'clsx'
import React, { createContext, useContext, useEffect, useState } from 'react'
import AppHeader from './Header'
import Sidebar from './Sidebar'

import { Outlet, useLocation } from 'react-router-dom'
import { cloneDeep, merge } from 'lodash'
import CoreSuspense from '@Core/Components/CoreSuspense'
import { useCoreRouterContext } from '@Core/Provider/CoreRouterProvider'
// Client-side cache, shared for the whole session of the user in the browser.
// import 'mathquill/build/mathquill'

const LayoutContext = createContext({
	layoutConfig: null,
	toggleCollapse: collapsed => 1,
	toggleOpenMobileSidebar: () => 1,
	openMobileSidebar: false,
	toggleVisibleSidebar: () => 1
})
export const useLayoutContext = () => useContext(LayoutContext)

const Layout = ({ appNavigationConfig = [], ...props }) => {
	const { route } = useCoreRouterContext()

	const [openMobileSidebar, setOpenMobileSidebar] = useState(false)
	const [layoutConfig, setLayoutConfig] = useState({ sidebar: { collapsed: false, display: false } })
	const location = useLocation()
	// console.log('🚀 ~ Layout ~ layoutConfig:', layoutConfig)

	const toggleCollapse = () => {
		setLayoutConfig(prev => ({
			...prev,
			sidebar: { ...prev.sidebar, collapsed: !prev.sidebar.collapsed }
		}))
	}

	const toggleVisibleSidebar = () => {
		setLayoutConfig(prev => ({ ...prev, sidebar: { ...prev.sidebar, display: !prev.sidebar.display } }))
	}
	const toggleOpenMobileSidebar = () => {
		setOpenMobileSidebar(prev => !prev)
	}

	const { mobile, tablet, laptop } = useBreakpoints()

	const routeSettings = route?.settings

	// console.log('============= route', route)
	useEffect(() => {
		const config = cloneDeep(routeSettings?.layout?.config)
		if (!laptop) {
			config.sidebar.collapsed = false
			setOpenMobileSidebar(false)
		}
		setLayoutConfig(prev => ({ ...prev, ...config }))
	}, [location.pathname])

	const layoutContext = {
		layoutConfig,
		toggleCollapse,
		toggleOpenMobileSidebar,
		openMobileSidebar,
		toggleVisibleSidebar,
		appNavigationConfig,
		...props
	}
	// console.log('🚀 ~ Layout ~ layoutContext.layoutConfig:', layoutContext.layoutConfig)

	return (
		<LayoutContext.Provider value={{ ...layoutContext }}>
			<div className="flex h-auto">
				<Sidebar />
				<div className="w-full overflow-auto">
					<AppHeader />
					<Box
						component="main"
						className={clsx('flex flex-col', {
							'h-[100vh]':
								(mobile && !layoutConfig?.mobileHeader?.display) || !layoutConfig?.header?.display,
							'h-[calc(100vh-60px)]': layoutConfig?.header?.display
						})}
					>
						<Box
							id="content-wrapper"
							className={clsx(' p-4 w-full h-full overflow-y-scroll scroll-smooth bg-gray-100', {
								'md:px-8 md:py-4': layoutConfig?.sidebar?.display
							})}
						>
							{/* {props.children} */}
							<CoreSuspense>
								<Outlet />
							</CoreSuspense>
						</Box>
					</Box>
				</div>
			</div>
		</LayoutContext.Provider>
	)
}
export default React.memo(Layout)
